/**
 * PageNav — universal sub-navigation that sits at the top of every content
 * page right under the global header.
 *
 *   ←  Back     ⌂  Home              Next: Atlas →
 *
 * Three slots:
 *   1. Back  — uses `navigate(-1)` when the visitor came from inside the
 *      app, otherwise falls back to the page's declared parent.
 *   2. Home  — always points at "/".
 *   3. Next  — context-aware suggestion from the route map below; lets us
 *      tell a story across pages (intelligence → atlas → marketplace, etc.).
 *
 * The route → next map lives in `pageNavMap` (sibling file) so adding a new
 * destination doesn't require touching this component.
 */
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Home, ChevronRight } from "lucide-react";
import { getPageNavConfig } from "./pageNavMap";

export default function PageNav() {
  const loc = useLocation();
  const nav = useNavigate();
  const config = getPageNavConfig(loc.pathname);

  // Don't render on the landing page — back/home/next are meaningless there.
  if (config.hidden) return null;

  const breadcrumbs = config.breadcrumbs || [];
  const next = config.next;
  const back = config.back; // { label, to } | undefined

  function handleBack() {
    if (back?.to) { nav(back.to); return; }
    // Cross-origin entries (search, direct link) have a single history entry —
    // fall back to home in that case.
    if (window.history.length > 2) nav(-1);
    else nav("/");
  }

  return (
    <div className="border-b border-border/40 bg-background/60 backdrop-blur-md" data-testid="page-nav">
      <div className="container-x flex h-11 items-center gap-3 text-xs">
        {/* Back */}
        <button
          onClick={handleBack}
          className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
          data-testid="page-nav-back"
        >
          <ArrowLeft size={13} /> {back?.label || "Back"}
        </button>

        <span className="text-muted-foreground/40">·</span>

        {/* Home */}
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
          data-testid="page-nav-home"
        >
          <Home size={13} /> Home
        </Link>

        {/* Breadcrumbs */}
        {breadcrumbs.length > 0 && (
          <>
            <span className="text-muted-foreground/40">·</span>
            <nav className="hidden md:flex items-center gap-1 text-muted-foreground" aria-label="Breadcrumb">
              {breadcrumbs.map((b, i) => (
                <span key={b.to || b.label} className="inline-flex items-center gap-1">
                  {b.to ? (
                    <Link to={b.to} className="hover:text-foreground transition-colors" data-testid={`page-nav-crumb-${i}`}>
                      {b.label}
                    </Link>
                  ) : (
                    <span className="text-foreground/80" data-testid={`page-nav-crumb-${i}`}>{b.label}</span>
                  )}
                  {i < breadcrumbs.length - 1 && <ChevronRight size={11} className="text-muted-foreground/40" />}
                </span>
              ))}
            </nav>
          </>
        )}

        {/* Next CTA */}
        {next && (
          <Link
            to={next.to}
            onClick={() => {
              // Lightweight analytics hook — fires if gtag (Google Analytics)
              // or window.dataLayer is loaded. No-op otherwise so we don't
              // crash on hosts without analytics.
              if (typeof window !== "undefined") {
                if (typeof window.gtag === "function") {
                  window.gtag("event", "pagenav_next", { from: loc.pathname, to: next.to, label: next.label });
                } else if (Array.isArray(window.dataLayer)) {
                  window.dataLayer.push({ event: "pagenav_next", from: loc.pathname, to: next.to, label: next.label });
                }
              }
            }}
            data-testid="page-nav-next"
            className="ml-auto inline-flex items-center gap-1 px-3 py-1 rounded-full border border-signal-lime/40 bg-signal-lime/5 text-signal-lime hover:bg-signal-lime/10 transition-colors"
          >
            <span className="text-muted-foreground font-mono uppercase tracking-[0.18em] text-[10px]">Next</span>
            <span className="font-medium">{next.label}</span>
            <ArrowRight size={12} />
          </Link>
        )}
      </div>
    </div>
  );
}
